/**
 * knowledgeStore — vibe-designer 1.170.0 Phase 3.
 *
 * Per-tenant cache of /api/knowledge/overview (Phase 2) PLUS per-service
 * search state (Phase 3). Mirrors tenantStore's idempotency-guard pattern.
 *
 * Per-service search state is keyed by ServiceName so 6 search panels can
 * fire concurrent requests without race conditions (one tab clicking
 * MIND while another tab searches CORTEX must not clobber each other).
 *
 * State:
 *   overview, isLoading, error                 (Phase 2)
 *   searchByService                             (Phase 3 — last result per service)
 *   isSearchingByService                        (Phase 3 — in-flight guard)
 *   searchErrorByService                        (Phase 3 — network error per service)
 *
 * Actions:
 *   fetchOverview()
 *   runSearch(service, q, limit?)               (idempotency-guarded per service)
 *   clearSearch(service)
 *   reset()
 */
import { create } from 'zustand';
import {
  fetchOverview as apiFetchOverview,
  searchService as apiSearchService,
  askKnowledge as apiAskKnowledge,
  searchAllServices as apiSearchAllServices,
  ingestKnowledge as apiIngestKnowledge,
  ingestPdf as apiIngestPdf,
  getIngestJobStatus as apiGetIngestJobStatus,
  isTerminalIngest,
  fetchRecentIngestRuns as apiFetchRecentIngestRuns,
  fetchObsidianPageList as apiFetchObsidianPageList,
  ingestUrl as apiIngestUrl,
  ingestRepo as apiIngestRepo,
  fetchSightRecent as apiFetchSightRecent,
  fetchMindStatsExtended as apiFetchMindStatsExtended,
  fetchCortexGraph as apiFetchCortexGraph,
  fetchMindGraph as apiFetchMindGraph,
  fetchObsidianGraph as apiFetchObsidianGraph,
  runWebSearch as apiRunWebSearch,
  fetchMindPillarBreakdown as apiFetchMindPillarBreakdown,
  fetchSynapseStatus as apiFetchSynapseStatus,
  compileSynapse as apiCompileSynapse,
  type SynapseStatusResponse,
  type SynapseCompileResponse,
  type WebSearchResponse,
  type MindPillarBreakdownResponse,
  type KnowledgeServiceStats,
  type KnowledgeSearchResponse,
  type KnowledgeAskResponse,
  type KnowledgeAllSearchResponse,
  type IngestRequest,
  type IngestResponse,
  type IngestRecentResponse,
  type ObsidianPageListResponse,
  type MindStatsExtendedResponse,
  type CortexGraphResponse,
  type MindGraphResponse,
  type ObsidianGraphResponse,
  type IngestUrlRequest,
  type IngestRepoRequest,
  type ActivityEvent,
  type SightRecentResponse,
} from '@/services/knowledgeApi';
import {
  type ClassifiedFile,
  FOLDER_MAX_FILES,
  FOLDER_MAX_TOTAL_BYTES,
  FOLDER_CONCURRENCY,
} from '@/utils/folderClassify';
import { ApiError } from '@/services/api';

type ServiceName = KnowledgeServiceStats['service'];

// Async scanned-PDF OCR poll cadence. The POST returns overall_status='processing'
// + a kp- job_id; the SPA polls the status route until terminal. Interval well
// under the marker-ocr ~19-32s/page warm rate; timeout under the backend async
// poll budget (1800s). Module-level so tests can override.
export const OCR_POLL_INTERVAL_MS = 3000;
export const OCR_POLL_TIMEOUT_MS = 30 * 60 * 1000; // 30 min

const _sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Poll GET /knowledge/ingest/{job_id}/status until the response is terminal
 * (overall_status !== 'processing'). Throws on a lost job (404 → backend
 * restart) or timeout so the folder settlement marks the file failed with an
 * honest message. Polls immediately first (a job that's already terminal
 * returns without a sleep).
 */
async function pollIngestUntilTerminal(
  jobId: string,
  intervalMs = OCR_POLL_INTERVAL_MS,
  timeoutMs = OCR_POLL_TIMEOUT_MS,
): Promise<IngestResponse> {
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    let resp: IngestResponse;
    try {
      resp = await apiGetIngestJobStatus(jobId);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        // Generalized 2026-06-28: this poller now also covers the CORTEX
        // async-confirm lane, not just scanned-PDF OCR.
        throw new Error('Ingest job was lost (service restarted) — please re-submit');
      }
      throw e;
    }
    if (isTerminalIngest(resp)) return resp;
    if (Date.now() >= deadline) {
      throw new Error('Ingest is taking longer than expected — check Recent Ingests');
    }
    await _sleep(intervalMs);
  }
}

// CORTEX async-confirm lane (2026-06-28): a direct text/URL/repo ingest whose
// CORTEX target is still materializing returns overall_status='processing'. The
// folder/PDF path already polls; this lets the NON-folder submit paths do the
// same WITHOUT freezing the form — show the processing card immediately
// (isIngesting already cleared), then poll in the background and overwrite the
// result card when it reaches a terminal state. Fire-and-forget by design.
function pollProcessingInBackground(
  response: IngestResponse,
  onSettled: (settled: IngestResponse) => void,
  onError: (err: Error) => void,
): void {
  if (response.overall_status !== 'processing') return;
  void pollIngestUntilTerminal(response.job_id).then(onSettled).catch(onError);
}

// 1.195.0 Phase 7.9 — per-file folder settlement record. Pass/fail status +
// optional error message used by the folder results banner to surface
// failing file names. App-level fail means EITHER the inner ingestKnowledge
// rejected (network/fetch error) OR the IngestResponse came back with
// overall_status !== 'completed' (e.g., SIGHT vLLM embed returned None).
export interface FolderSettlement {
  sourceName: string;
  status: 'completed' | 'failed';
  error: string | null;
}

const SERVICES: ServiceName[] = [
  'mind', 'cortex', 'brain', 'sight', 'cognition', 'obsidian',
];

const emptyServiceMap = <T,>(value: T): Record<ServiceName, T> =>
  SERVICES.reduce((acc, s) => {
    acc[s] = value;
    return acc;
  }, {} as Record<ServiceName, T>);

interface KnowledgeState {
  overview: KnowledgeServiceStats[] | null;
  isLoading: boolean;
  error: Error | null;

  searchByService: Record<ServiceName, KnowledgeSearchResponse | null>;
  isSearchingByService: Record<ServiceName, boolean>;
  searchErrorByService: Record<ServiceName, Error | null>;

  // 1.171.0 Phase 4 — Ask box
  askResponse: KnowledgeAskResponse | null;
  isAsking: boolean;
  askError: Error | null;

  // 1.171.0 Phase 4 — All-services search
  allSearchResponse: KnowledgeAllSearchResponse | null;
  isSearchingAll: boolean;
  allSearchError: Error | null;

  // 1.172.0 Phase 5α — Ingest
  ingestResponse: IngestResponse | null;
  isIngesting: boolean;
  ingestError: Error | null;

  // 1.194.0 Phase 7.8 — Folder ingest (parallel 5-wide pool over classified
  // files). Separate flag from `isIngesting` so per-file folder errors don't
  // clobber the single-shot text/url/repo/image batches' error state.
  // 1.195.0 Phase 7.9 — FolderSettlement carries per-file pass/fail + error
  // message so the results banner can surface failing file names.
  isIngestingFolder: boolean;
  folderIngestProgress: { done: number; total: number; errors: number } | null;
  folderIngestResults: FolderSettlement[] | null;
  folderIngestError: Error | null;

  // 1.173.0 Phase 5β1 — Monitoring
  recentRuns: IngestRecentResponse | null;
  isFetchingRecentRuns: boolean;
  recentRunsError: Error | null;
  obsidianPages: ObsidianPageListResponse | null;
  isFetchingObsidianPages: boolean;
  obsidianPagesError: Error | null;

  // 1.174.0 Phase 5β2 — Viz layer (MIND chunks-per-day + CORTEX entity graph)
  mindStatsExtended: MindStatsExtendedResponse | null;
  isFetchingMindStatsExtended: boolean;
  mindStatsExtendedError: Error | null;
  cortexGraph: CortexGraphResponse | null;
  isFetchingCortexGraph: boolean;
  cortexGraphError: Error | null;
  // WS-B1 — MIND document-level similarity graph (reuses the graph canvas).
  mindGraph: MindGraphResponse | null;
  isFetchingMindGraph: boolean;
  mindGraphError: Error | null;

  // 1.175.0 Phase 5β3 — OBSIDIAN backlink graph
  obsidianGraph: ObsidianGraphResponse | null;
  isFetchingObsidianGraph: boolean;
  obsidianGraphError: Error | null;

  // 1.178.0 Phase 5γ.2 — SIGHT image grid
  sightRecent: SightRecentResponse | null;
  isFetchingSightRecent: boolean;
  sightRecentError: Error | null;

  // 1.180.0 Phase 6.3 — SSE activity feed. Newest-first, capped at 50.
  activityEvents: ActivityEvent[];

  // 1.191.0 Phase 7.6.1 — SSE-driven count refresh trigger. Monotonic
  // counter incremented on each non-system ingest event. The 7 count-
  // bearing widgets (overview stat grid + 6 detail viz) include this in
  // their useEffect dep array so they re-fetch automatically when the SSE
  // feed delivers a new ingest. System (continuous_injector heartbeat)
  // events do NOT increment — they're not new corpus content.
  //
  // R2 #1 fix: monotonic counter, NOT Date.now(). Two SSE events within
  // the same millisecond produce identical Date.now() scalars, which
  // Zustand short-circuits via Object.is — the widgets' useEffect would
  // not re-run for the second event. The counter guarantees strict
  // inequality on every distinct non-deduped push.
  //
  // R2 #4: in-flight idempotency guards in each fetchX action will
  // silently drop a counter-triggered re-fetch if a prior fetch is still
  // in flight. This is intentional — the in-flight fetch will return
  // fresh data anyway — but it means rapid SSE bursts may produce fewer
  // visible re-renders than counter increments.
  lastIngestAt: number | null;

  // 1.185.0 Phase 7.2 — Web Search (SearXNG) results state.
  webSearchResults: WebSearchResponse | null;
  isWebSearching: boolean;
  webSearchError: Error | null;

  // 1.190.0 Phase 7.6 — MIND pillar inventory state for Overview.
  mindPillarBreakdown: MindPillarBreakdownResponse | null;
  isFetchingMindPillarBreakdown: boolean;
  mindPillarBreakdownError: Error | null;

  // 2026-07-02 — THE SYNAPSE (knowledge loop) state. Not a corpus: status
  // comes from its own route, not /overview.
  synapseStatus: SynapseStatusResponse | null;
  isFetchingSynapseStatus: boolean;
  synapseStatusError: Error | null;
  isSynapseCompiling: boolean;
  synapseCompileResult: SynapseCompileResponse | null;
  synapseCompileError: string | null;

  fetchOverview: () => Promise<void>;
  runSearch: (service: ServiceName, q: string, limit?: number, opts?: { pillar_filter?: string | null }) => Promise<void>;
  clearSearch: (service: ServiceName) => void;
  runAsk: (q: string, opts?: { pillar_filter?: string | null; model?: string; thinking?: boolean }) => Promise<void>;
  runAllSearch: (q: string, limit_per_service?: number, opts?: { pillar_filter?: string | null }) => Promise<void>;
  runIngest: (req: IngestRequest) => Promise<void>;
  runIngestUrl: (req: IngestUrlRequest) => Promise<void>;
  runIngestRepo: (req: IngestRepoRequest) => Promise<void>;
  runIngestFolder: (
    files: ClassifiedFile[],
    opts: { collection: string; pillar?: string | null; textTargets?: string[] },
  ) => Promise<void>;
  fetchRecentRuns: (limit?: number) => Promise<void>;
  fetchObsidianPageList: (limit?: number) => Promise<void>;
  fetchMindStatsExtended: () => Promise<void>;
  fetchCortexGraph: (limit?: number) => Promise<void>;
  fetchMindGraph: (limit?: number) => Promise<void>;
  fetchObsidianGraph: (limit?: number) => Promise<void>;
  fetchSightRecent: (limit?: number) => Promise<void>;
  pushActivityEvent: (event: ActivityEvent) => void;
  runWebSearch: (q: string, max_results?: number) => Promise<void>;
  clearWebSearch: () => void;
  fetchMindPillarBreakdown: () => Promise<void>;
  fetchSynapseStatus: () => Promise<void>;
  runSynapseCompile: () => Promise<void>;
  reset: () => void;
}

async function ingestOneFolderFile(
  cf: ClassifiedFile,
  opts: { collection: string; pillar?: string | null; textTargets?: string[] },
): Promise<IngestResponse> {
  // Resolve target services. Images ALWAYS go to SIGHT (image embeddings).
  // Text-capable files (pdf/markdown/text/code) honor the caller's selected
  // `textTargets` (the Targets checkboxes) when supplied — SIGHT is dropped
  // since it is image-only; falls back to the per-file classifier target
  // (mind) only when no selection is passed (e.g. store-level callers that omit
  // textTargets; both the folder + doc pickers now pass it).
  const textTargets = (opts.textTargets ?? []).filter((t) => t !== 'sight');
  const targets =
    cf.contentType === 'image'
      ? [cf.targetService!]
      : textTargets.length > 0
        ? textTargets
        : [cf.targetService!];

  // PDF: upload raw bytes — the backend extracts the text layer (pypdf) and
  // ingests it. Cannot be read client-side as text/data-URL like the other
  // branches, so it takes the dedicated multipart endpoint.
  if (cf.contentType === 'pdf') {
    const resp = await apiIngestPdf({
      file: cf.file,
      source_name: cf.sourceName,
      target_services: targets,
      collection: opts.collection,
      ...(opts.pillar ? { pillar: opts.pillar } : {}),
    });
    // A scanned/image-only PDF takes the async OCR lane: the POST returns
    // overall_status='processing' + a kp- job_id. Poll until terminal so the
    // caller (folder settlement) sees a completed/partial/failed result, not the
    // non-terminal 'processing' sentinel. Digital PDFs return terminal already.
    if (resp.overall_status === 'processing') {
      return pollIngestUntilTerminal(resp.job_id);
    }
    return resp;
  }
  // FileReader path for both text + image branches. jsdom's File.prototype.text()
  // is unreliable across versions; FileReader is the spec-stable path and is
  // already used elsewhere in this SPA (KnowledgeIngestPanel submitImageBatch).
  const content =
    cf.contentType === 'image' ? await readFileAs(cf.file, 'dataurl') : await readFileAs(cf.file, 'text');
  return apiIngestKnowledge({
    content,
    source_name: cf.sourceName,
    // Runtime values are valid service names (from the Targets checkboxes or
    // the per-file classifier); cast to the IngestRequest union.
    target_services: targets as IngestRequest['target_services'],
    collection: opts.collection,
    content_type: cf.contentType!,
    ...(opts.pillar ? { metadata: { pillar: opts.pillar } } : {}),
  });
}

function readFileAs(file: File, mode: 'text' | 'dataurl'): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    if (mode === 'text') reader.readAsText(file);
    else reader.readAsDataURL(file);
  });
}

export const useKnowledgeStore = create<KnowledgeState>((set, get) => ({
  overview: null,
  isLoading: false,
  error: null,
  searchByService: emptyServiceMap<KnowledgeSearchResponse | null>(null),
  isSearchingByService: emptyServiceMap<boolean>(false),
  searchErrorByService: emptyServiceMap<Error | null>(null),
  askResponse: null,
  isAsking: false,
  askError: null,
  allSearchResponse: null,
  isSearchingAll: false,
  allSearchError: null,
  ingestResponse: null,
  isIngesting: false,
  ingestError: null,
  isIngestingFolder: false,
  folderIngestProgress: null,
  folderIngestResults: null,
  folderIngestError: null,
  recentRuns: null,
  isFetchingRecentRuns: false,
  recentRunsError: null,
  obsidianPages: null,
  isFetchingObsidianPages: false,
  obsidianPagesError: null,
  mindStatsExtended: null,
  isFetchingMindStatsExtended: false,
  mindStatsExtendedError: null,
  cortexGraph: null,
  isFetchingCortexGraph: false,
  cortexGraphError: null,
  mindGraph: null,
  isFetchingMindGraph: false,
  mindGraphError: null,
  obsidianGraph: null,
  isFetchingObsidianGraph: false,
  obsidianGraphError: null,
  sightRecent: null,
  isFetchingSightRecent: false,
  sightRecentError: null,
  activityEvents: [],
  lastIngestAt: null,

  // 1.185.0 Phase 7.2 — Web Search state.
  webSearchResults: null,
  isWebSearching: false,
  webSearchError: null,

  // 1.190.0 Phase 7.6 — MIND pillar inventory state.
  mindPillarBreakdown: null,
  isFetchingMindPillarBreakdown: false,
  mindPillarBreakdownError: null,

  // 2026-07-02 — THE SYNAPSE state.
  synapseStatus: null,
  isFetchingSynapseStatus: false,
  synapseStatusError: null,
  isSynapseCompiling: false,
  synapseCompileResult: null,
  synapseCompileError: null,

  fetchOverview: async () => {
    if (get().isLoading) return;
    set({ isLoading: true, error: null });
    try {
      const response = await apiFetchOverview();
      set({
        overview: response.services,
        isLoading: false,
        error: null,
      });
    } catch (err) {
      set({
        overview: null,
        isLoading: false,
        error: err as Error,
      });
    }
  },

  runSearch: async (service, q, limit = 10, opts) => {
    // R2 #2: idempotency guard. The SPA form disables the submit button
    // while isSearching is true, so this branch is unreachable from the
    // normal click path. It exists for programmatic callers (tests + any
    // future deep-link / keyboard-shortcut wiring).
    if (get().isSearchingByService[service]) return;
    set((s) => ({
      isSearchingByService: { ...s.isSearchingByService, [service]: true },
      searchErrorByService: { ...s.searchErrorByService, [service]: null },
    }));
    try {
      const response = await apiSearchService(service, q, limit, opts);
      set((s) => ({
        searchByService: { ...s.searchByService, [service]: response },
        isSearchingByService: { ...s.isSearchingByService, [service]: false },
        searchErrorByService: { ...s.searchErrorByService, [service]: null },
      }));
    } catch (err) {
      set((s) => ({
        searchByService: { ...s.searchByService, [service]: null },
        isSearchingByService: { ...s.isSearchingByService, [service]: false },
        searchErrorByService: { ...s.searchErrorByService, [service]: err as Error },
      }));
    }
  },

  clearSearch: (service) => {
    set((s) => ({
      searchByService: { ...s.searchByService, [service]: null },
      isSearchingByService: { ...s.isSearchingByService, [service]: false },
      searchErrorByService: { ...s.searchErrorByService, [service]: null },
    }));
  },

  runAsk: async (q, opts) => {
    // R2 #2: idempotency guard. Unreachable from the SPA form (submit button is
    // disabled while isAsking). Exists for programmatic callers.
    if (get().isAsking) return;
    set({ isAsking: true, askError: null });
    try {
      const response = await apiAskKnowledge(q, opts);
      set({ askResponse: response, isAsking: false, askError: null });
    } catch (err) {
      set({ askResponse: null, isAsking: false, askError: err as Error });
    }
  },

  runAllSearch: async (q, limit_per_service = 5, opts) => {
    if (get().isSearchingAll) return;
    set({ isSearchingAll: true, allSearchError: null });
    try {
      const response = await apiSearchAllServices(q, limit_per_service, opts);
      set({ allSearchResponse: response, isSearchingAll: false, allSearchError: null });
    } catch (err) {
      set({ allSearchResponse: null, isSearchingAll: false, allSearchError: err as Error });
    }
  },

  runIngest: async (req) => {
    // Idempotency guard — unreachable from SPA form (submit button disabled
    // during isIngesting). Defends programmatic callers.
    if (get().isIngesting) return;
    set({ isIngesting: true, ingestError: null });
    try {
      const response = await apiIngestKnowledge(req);
      set({ ingestResponse: response, isIngesting: false, ingestError: null });
      // 1.173.0 Phase 5β1: auto-refresh recent runs after a successful ingest
      // so the user sees the new job appear without a manual refresh.
      void get().fetchRecentRuns();
      pollProcessingInBackground(
        response,
        (settled) => {
          if (get().ingestResponse?.job_id === settled.job_id) set({ ingestResponse: settled });
          void get().fetchRecentRuns();
        },
        (err) => {
          if (get().ingestResponse?.job_id === response.job_id) set({ ingestError: err });
        },
      );
    } catch (err) {
      set({ ingestResponse: null, isIngesting: false, ingestError: err as Error });
    }
  },

  // 1.177.0 Phase 6.1 — URL ingest. Mirrors runIngest with the same
  // idempotency guard + auto-refresh; same isIngesting flag so the
  // submit button is disabled for both text + URL paths.
  runIngestUrl: async (req) => {
    if (get().isIngesting) return;
    set({ isIngesting: true, ingestError: null });
    try {
      const response = await apiIngestUrl(req);
      set({ ingestResponse: response, isIngesting: false, ingestError: null });
      void get().fetchRecentRuns();
      pollProcessingInBackground(
        response,
        (settled) => {
          if (get().ingestResponse?.job_id === settled.job_id) set({ ingestResponse: settled });
          void get().fetchRecentRuns();
        },
        (err) => {
          if (get().ingestResponse?.job_id === response.job_id) set({ ingestError: err });
        },
      );
    } catch (err) {
      set({ ingestResponse: null, isIngesting: false, ingestError: err as Error });
    }
  },

  // 1.179.0 Phase 6.2.1 — code-repo ingest. Mirrors runIngestUrl with the
  // same idempotency guard + auto-refresh. The backend orchestrator passes
  // skip_cache=true to per-file ingest_now so the Recent Ingests widget
  // only sees the outer kr-* aggregate, not N per-file entries.
  runIngestRepo: async (req) => {
    if (get().isIngesting) return;
    set({ isIngesting: true, ingestError: null });
    try {
      const response = await apiIngestRepo(req);
      set({ ingestResponse: response, isIngesting: false, ingestError: null });
      void get().fetchRecentRuns();
      pollProcessingInBackground(
        response,
        (settled) => {
          if (get().ingestResponse?.job_id === settled.job_id) set({ ingestResponse: settled });
          void get().fetchRecentRuns();
        },
        (err) => {
          if (get().ingestResponse?.job_id === response.job_id) set({ ingestError: err });
        },
      );
    } catch (err) {
      set({ ingestResponse: null, isIngesting: false, ingestError: err as Error });
    }
  },

  // 1.194.0 Phase 7.8 — folder ingest. Parallel 5-wide async-pool over
  // pre-classified files. Per-file routing (MIND/SIGHT) from the classifier;
  // continue-on-error per file (no short-circuit); single shared progress
  // counter; aggregate caps enforced pre-launch.
  runIngestFolder: async (files, opts) => {
    if (get().isIngestingFolder) return;
    const supported = files.filter((f) => f.targetService !== null);
    if (supported.length > FOLDER_MAX_FILES) {
      set({
        folderIngestError: new Error(
          `folder exceeds ${FOLDER_MAX_FILES} files cap (${supported.length} supported)`,
        ),
        folderIngestProgress: null,
        folderIngestResults: null,
      });
      return;
    }
    const totalBytes = supported.reduce((sum, f) => sum + f.file.size, 0);
    if (totalBytes > FOLDER_MAX_TOTAL_BYTES) {
      set({
        folderIngestError: new Error(
          `folder exceeds 100MB total bytes cap (${(totalBytes / 1024 / 1024).toFixed(1)}MB)`,
        ),
        folderIngestProgress: null,
        folderIngestResults: null,
      });
      return;
    }
    set({
      isIngestingFolder: true,
      folderIngestError: null,
      folderIngestProgress: { done: 0, total: supported.length, errors: 0 },
      folderIngestResults: null,
    });

    const queue = [...supported];
    let running = 0;
    let errors = 0;
    const settled: FolderSettlement[] = [];

    await new Promise<void>((resolve) => {
      function tryLaunch() {
        while (running < FOLDER_CONCURRENCY && queue.length > 0) {
          const cf = queue.shift()!;
          running++;
          ingestOneFolderFile(cf, opts)
            .catch((err: unknown) => (err instanceof Error ? err : new Error(String(err))))
            .then((maybeError) => {
              running--;
              // 1.195.0 Phase 7.9 — app-level fail detection. Either the inner
              // call threw (Error path) OR the IngestResponse came back with
              // overall_status !== 'completed' (per_service errors).
              const failed =
                maybeError instanceof Error ||
                (maybeError as IngestResponse).overall_status !== 'completed';
              const errorMsg = failed
                ? maybeError instanceof Error
                  ? maybeError.message
                  : (maybeError as IngestResponse).per_service
                      .filter((r) => r.error)
                      .map((r) => `${r.service}: ${r.error}`)
                      .join('; ') || null
                : null;
              if (failed) errors++;
              settled.push({
                sourceName: cf.sourceName,
                status: failed ? 'failed' : 'completed',
                error: errorMsg,
              });
              set((s) => ({
                folderIngestProgress: s.folderIngestProgress
                  ? {
                      done: s.folderIngestProgress.done + 1,
                      total: s.folderIngestProgress.total,
                      errors,
                    }
                  : null,
              }));
              if (queue.length === 0 && running === 0) resolve();
              else tryLaunch();
            });
        }
        if (queue.length === 0 && running === 0) resolve();
      }
      tryLaunch();
    });

    set({ isIngestingFolder: false, folderIngestResults: settled });
    void get().fetchRecentRuns();
  },

  fetchRecentRuns: async (limit = 20) => {
    if (get().isFetchingRecentRuns) return;
    set({ isFetchingRecentRuns: true, recentRunsError: null });
    try {
      const response = await apiFetchRecentIngestRuns(limit);
      set({ recentRuns: response, isFetchingRecentRuns: false, recentRunsError: null });
    } catch (err) {
      set({ recentRuns: null, isFetchingRecentRuns: false, recentRunsError: err as Error });
    }
  },

  fetchObsidianPageList: async (limit = 50) => {
    if (get().isFetchingObsidianPages) return;
    set({ isFetchingObsidianPages: true, obsidianPagesError: null });
    try {
      const response = await apiFetchObsidianPageList(limit);
      set({ obsidianPages: response, isFetchingObsidianPages: false, obsidianPagesError: null });
    } catch (err) {
      set({ obsidianPages: null, isFetchingObsidianPages: false, obsidianPagesError: err as Error });
    }
  },

  fetchMindStatsExtended: async () => {
    if (get().isFetchingMindStatsExtended) return;
    set({ isFetchingMindStatsExtended: true, mindStatsExtendedError: null });
    try {
      const response = await apiFetchMindStatsExtended();
      set({ mindStatsExtended: response, isFetchingMindStatsExtended: false, mindStatsExtendedError: null });
    } catch (err) {
      set({ mindStatsExtended: null, isFetchingMindStatsExtended: false, mindStatsExtendedError: err as Error });
    }
  },

  fetchCortexGraph: async (limit = 50) => {
    if (get().isFetchingCortexGraph) return;
    set({ isFetchingCortexGraph: true, cortexGraphError: null });
    try {
      const response = await apiFetchCortexGraph(limit);
      set({ cortexGraph: response, isFetchingCortexGraph: false, cortexGraphError: null });
    } catch (err) {
      set({ cortexGraph: null, isFetchingCortexGraph: false, cortexGraphError: err as Error });
    }
  },

  fetchMindGraph: async (limit = 60000) => {
    if (get().isFetchingMindGraph) return;
    set({ isFetchingMindGraph: true, mindGraphError: null });
    try {
      const response = await apiFetchMindGraph(limit);
      set({ mindGraph: response, isFetchingMindGraph: false, mindGraphError: null });
    } catch (err) {
      set({ mindGraph: null, isFetchingMindGraph: false, mindGraphError: err as Error });
    }
  },

  fetchObsidianGraph: async (limit = 100) => {
    if (get().isFetchingObsidianGraph) return;
    set({ isFetchingObsidianGraph: true, obsidianGraphError: null });
    try {
      const response = await apiFetchObsidianGraph(limit);
      set({ obsidianGraph: response, isFetchingObsidianGraph: false, obsidianGraphError: null });
    } catch (err) {
      set({ obsidianGraph: null, isFetchingObsidianGraph: false, obsidianGraphError: err as Error });
    }
  },

  fetchSightRecent: async (limit = 24) => {
    if (get().isFetchingSightRecent) return;
    set({ isFetchingSightRecent: true, sightRecentError: null });
    try {
      const response = await apiFetchSightRecent(limit);
      set({ sightRecent: response, isFetchingSightRecent: false, sightRecentError: null });
    } catch (err) {
      set({ sightRecent: null, isFetchingSightRecent: false, sightRecentError: err as Error });
    }
  },

  // 1.180.0 Phase 6.3 — SSE activity feed event sink. Newest-first; cap 50.
  // De-duplicates on event_id so a flaky reconnect doesn't double-render.
  //
  // 1.191.0 Phase 7.6.1: also increments `lastIngestAt` to drive count-
  // widget re-fetch. Dedup early-return MUST stay first so a replayed
  // event_id does NOT re-bump the counter (otherwise a flaky SSE
  // reconnect would over-fetch). Gate `event.event_type !== 'system'`
  // excludes the continuous_injector heartbeat — it's not new corpus
  // content.
  //
  // R2 #1 fix: monotonic counter (NOT Date.now()) to avoid Zustand
  // Object.is short-circuit on same-millisecond SSE bursts.
  pushActivityEvent: (event) => {
    const current = get().activityEvents;
    if (current.some((e) => e.event_id === event.event_id)) return;
    const next = [event, ...current].slice(0, 50);
    if (event.event_type !== 'system') {
      set((s) => ({ activityEvents: next, lastIngestAt: (s.lastIngestAt ?? 0) + 1 }));
    } else {
      set({ activityEvents: next });
    }
  },

  // 1.185.0 Phase 7.2 — Web Search action. Mirrors runAsk idempotency
  // guard. Honest fallback: results=[] could be "no hits" OR backend
  // graceful-degrade on SearXNG-down — pair with mcpCapabilities.web_search
  // from tenantStore for the up/down signal.
  runWebSearch: async (q, max_results = 5) => {
    if (get().isWebSearching) return;
    set({ isWebSearching: true, webSearchError: null });
    try {
      const response = await apiRunWebSearch(q, max_results);
      set({ webSearchResults: response, isWebSearching: false, webSearchError: null });
    } catch (err) {
      set({ webSearchResults: null, isWebSearching: false, webSearchError: err as Error });
    }
  },

  clearWebSearch: () => {
    set({ webSearchResults: null, isWebSearching: false, webSearchError: null });
  },

  // 2026-07-02 — SYNAPSE loop status. Mirrors the fetchOverview in-flight
  // idempotency guard.
  fetchSynapseStatus: async () => {
    if (get().isFetchingSynapseStatus) return;
    set({ isFetchingSynapseStatus: true, synapseStatusError: null });
    try {
      const status = await apiFetchSynapseStatus();
      set({ synapseStatus: status, isFetchingSynapseStatus: false });
    } catch (err) {
      set({
        synapseStatus: null,
        isFetchingSynapseStatus: false,
        synapseStatusError: err as Error,
      });
    }
  },

  // 2026-07-02 — manual SYNAPSE compile, then status re-fetch so the
  // article inventory reflects the run. `synapseCompileError` carries BOTH
  // transport failures and the backend's honest ok=false error string.
  runSynapseCompile: async () => {
    if (get().isSynapseCompiling) return;
    set({
      isSynapseCompiling: true,
      synapseCompileResult: null,
      synapseCompileError: null,
    });
    try {
      const result = await apiCompileSynapse();
      set({
        isSynapseCompiling: false,
        synapseCompileResult: result,
        synapseCompileError: result.ok ? null : result.error ?? 'compile failed',
      });
    } catch (err) {
      set({
        isSynapseCompiling: false,
        synapseCompileResult: null,
        synapseCompileError: (err as Error).message,
      });
      return;
    }
    // Refresh loop state after the run (guard-bypassing direct fetch so a
    // concurrent status poll can't starve the post-compile refresh).
    try {
      const status = await apiFetchSynapseStatus();
      set({ synapseStatus: status });
    } catch {
      // Status refresh is best-effort; the compile result is already shown.
    }
  },

  // 1.190.0 Phase 7.6 — MIND pillar inventory action. Idempotent guard
  // mirrors fetchMindStatsExtended; honest-fallback pattern preserves
  // available + pillars + error fields from the backend response.
  fetchMindPillarBreakdown: async () => {
    if (get().isFetchingMindPillarBreakdown) return;
    set({ isFetchingMindPillarBreakdown: true, mindPillarBreakdownError: null });
    try {
      const response = await apiFetchMindPillarBreakdown();
      set({
        mindPillarBreakdown: response,
        isFetchingMindPillarBreakdown: false,
        mindPillarBreakdownError: null,
      });
    } catch (err) {
      set({
        mindPillarBreakdown: null,
        isFetchingMindPillarBreakdown: false,
        mindPillarBreakdownError: err as Error,
      });
    }
  },

  reset: () =>
    set({
      overview: null,
      isLoading: false,
      error: null,
      searchByService: emptyServiceMap<KnowledgeSearchResponse | null>(null),
      isSearchingByService: emptyServiceMap<boolean>(false),
      searchErrorByService: emptyServiceMap<Error | null>(null),
      askResponse: null,
      isAsking: false,
      askError: null,
      allSearchResponse: null,
      isSearchingAll: false,
      allSearchError: null,
      ingestResponse: null,
      isIngesting: false,
      ingestError: null,
      isIngestingFolder: false,
      folderIngestProgress: null,
      folderIngestResults: null,
      folderIngestError: null,
      recentRuns: null,
      isFetchingRecentRuns: false,
      recentRunsError: null,
      obsidianPages: null,
      isFetchingObsidianPages: false,
      obsidianPagesError: null,
      mindStatsExtended: null,
      isFetchingMindStatsExtended: false,
      mindStatsExtendedError: null,
      cortexGraph: null,
      isFetchingCortexGraph: false,
      cortexGraphError: null,
      obsidianGraph: null,
      isFetchingObsidianGraph: false,
      obsidianGraphError: null,
      sightRecent: null,
      isFetchingSightRecent: false,
      sightRecentError: null,
      activityEvents: [],
      lastIngestAt: null,
      webSearchResults: null,
      isWebSearching: false,
      webSearchError: null,
      mindPillarBreakdown: null,
      isFetchingMindPillarBreakdown: false,
      mindPillarBreakdownError: null,
      synapseStatus: null,
      isFetchingSynapseStatus: false,
      synapseStatusError: null,
      isSynapseCompiling: false,
      synapseCompileResult: null,
      synapseCompileError: null,
    }),
}));
